import { ChangeEvent, useContext, useEffect, useState } from "react";
import {
  Avatar,
  Backdrop,
  Box,
  CircularProgress,
  Container,
  CssBaseline,
  InputLabel,
  Stack,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { loadRecord, saveUser } from "../api/connection";
import { PageContext } from "../contexts/PageContext";
import { User } from "../models/User";
import { Org } from "../models/Org";
import { Save } from "@mui/icons-material";
import PageHeader from "../components/PageHeader";
import Minibar from "../components/Minibar";
import { getRoleColor } from "../utils/UserRoles";

export default function EditPersonalAccount() {
  const { user, setUser } = useContext(PageContext);
  const [currentUser, setCurrentUser] = useState<User>(
    user ||
      ({
        name: "",
        email: "",
        tenantEmail: "",
        role: "",
        orgId: "",
      } as unknown as User)
  );
  const [organization, setOrganization] = useState<Org | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      const setData = async function setData(user: User): Promise<void> {
        setCurrentUser(user);
        const org = await loadRecord<Org>("organizations", user.orgId);
        if (org) {
          setOrganization(org);
        }
      };
      setData(user);
    }
  }, [user]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCurrentUser((prevUser) => ({ ...prevUser, [name]: value }));
  };

  const handleSaveUser = async () => {
    if (!currentUser) {
      return;
    }
    const user = currentUser;
    if (!user.name || !user.email || !user.tenantEmail) {
      alert("Please fill in all required fields.");
      return;
    }
    setLoading(true);

    try {
      const res = await saveUser(currentUser);
      setUser(res);
    } catch (err) {
      console.error("Failed to save user:", err);
      alert(
        "An unexpected error occurred. Please try again or contact support."
      );
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (fullName: string | undefined) => {
    if (!fullName || typeof fullName !== "string") {
      return "";
    }
    const names = fullName.split(" ");
    const initials = names
      .filter((name) => name.length > 0)
      .map((name) => name[0].toUpperCase())
      .join("");
    return initials.slice(0, 2);
  };

  if (!currentUser || !user) {
    return (
      <Backdrop open={true} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />

      {/* page header */}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          m: 0,
          backgroundColor: "#212121",
          color: "#ECECEC",
          minHeight: "100vh",
        }}
      >
        <Backdrop
          open={loading || !organization}
          sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
        <PageHeader
          title="My Account"
          rightComponent={[
            <Minibar
              minibarItems={[
                {
                  text: "Save",
                  icon: <Save />,
                  onClick: () => {
                    handleSaveUser();
                  },
                },
              ]}
              barLength={2}
            />,
          ]}
        />

        <Container maxWidth="md">
          <Grid
            container
            spacing={3}
            justifyContent="center"
            alignItems="center"
          >
            <Grid size={12}>
              <Grid container spacing={3}>
                <Grid size={12}>
                  <Stack spacing={3} alignItems="center">
                    <Avatar
                      sx={{
                        bgcolor: getRoleColor(currentUser.role),
                        color: "white",
                        width: 70,
                        height: 70,
                        fontSize: 70 / 2.5,
                      }}
                    >
                      {getInitials(currentUser.name)}
                    </Avatar>
                  </Stack>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="user-name">Full Name</InputLabel>
                    <TextField
                      fullWidth
                      value={currentUser.name || ""}
                      id="user-name"
                      placeholder="Full Name"
                      autoFocus
                      name="name"
                      onChange={handleInputChange}
                    />
                  </Stack>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="user-role">Role</InputLabel>
                    <TextField
                      fullWidth
                      value={currentUser.role || ""}
                      id="user-role"
                      placeholder="Role"
                      name="role"
                      slotProps={{ htmlInput: { readOnly: true } }}
                    />
                  </Stack>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="oraganization">
                      Organization
                    </InputLabel>
                    <TextField
                      fullWidth
                      value={organization?.name || ""}
                      id="oraganization"
                      placeholder="Organization"
                      name="organization"
                      slotProps={{ htmlInput: { readOnly: true } }}
                    />
                  </Stack>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="user-contactEmail">Email</InputLabel>
                    <TextField
                      type="email"
                      fullWidth
                      value={currentUser.email || ""}
                      id="user-contactEmail"
                      placeholder="Enter Email"
                      name="contactEmail"
                      slotProps={{ htmlInput: { readOnly: true } }}
                    />
                  </Stack>
                </Grid>
                <Grid size={12}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="user-email">Tenant Email</InputLabel>
                    <TextField
                      variant="outlined"
                      value={currentUser.tenantEmail || ""}
                      slotProps={{ htmlInput: { readOnly: true } }}
                      fullWidth
                    />
                  </Stack>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
}
